import type { BillStatus } from '../../../types/bills';
import { type Action, allow, type Decision, deny, type PolicyDenialCode } from '../types';

/**
 * The bill-lock rule every order charge shares.
 *
 *   own + child Bill statuses ──▶ any ∉ {DRAFT, CANCELLED} ? ──▶ locked
 *
 * The predicate is one, and so is the sentence: every owner denies with
 * `ORDER_CHARGE_BILL_LOCKED_MESSAGE`, under its own code.
 */
export const ORDER_CHARGE_EDITABLE_BILL_STATUSES: readonly BillStatus[] = ['DRAFT', 'CANCELLED'];

export const isOrderChargeBillLocked = (billStatuses: readonly BillStatus[]): boolean =>
  billStatuses.some((status) => !ORDER_CHARGE_EDITABLE_BILL_STATUSES.includes(status));

export const orderChargeBillLock = (
  billStatuses: readonly BillStatus[],
  code: PolicyDenialCode,
  message: string
): Decision => (isOrderChargeBillLocked(billStatuses) ? deny(code, message) : allow());

/** The ONE sentence a bill-locked charge answers with, on every owner, in the grid and on the wire. */
export const ORDER_CHARGE_BILL_LOCKED_MESSAGE = 'This charge is on an open invoice or payable.';

/**
 * The delivered-price gate the api has always asked FIRST, for a charge on any host:
 *
 *   `includedInDlvPrice` sent ─▶ the charge has ANY bill line (any bill status) ─▶ refused
 *
 * Not the bill lock above: a DRAFT or CANCELLED bill line still refuses it. The sentence is the
 * shared message, without a recovery instruction: any bill line still locks the field.
 */
export const ORDER_CHARGE_DELIVERED_PRICE_BILL_OPEN_MESSAGE = 'The bill is open.';

export const orderChargeDeliveredPriceBillLock = (chargeHasBillLine: boolean): Decision =>
  chargeHasBillLine
    ? deny('ORDER_CHARGE_DELIVERED_PRICE_BILL_OPEN', ORDER_CHARGE_DELIVERED_PRICE_BILL_OPEN_MESSAGE)
    : allow();

/** Owner-neutral name for the bill-lock code the charges grid has always carried on the wire. */
export const ORDER_EXPENSE_BILL_LOCKED = 'SALES_ORDER_EXPENSE_BILL_LOCKED' satisfies PolicyDenialCode;

/** The charge columns a host charge policy judges — the charges grid's own list. */
export const HOST_CHARGE_FIELDS = [
  'expenseType',
  'accountLevel',
  'ledgerAccount',
  'businessEntity',
  'chargePer',
  'quantity',
  'unitAmount',
  'includedInDlvPrice',
  'description',
  'linkedLineItems',
] as const;

export type HostChargeField = (typeof HOST_CHARGE_FIELDS)[number];

export const HOST_CHARGE_OPS = ['clear', 'duplicate', 'repair incomplete allocation'] as const;
export type HostChargeOp = (typeof HOST_CHARGE_OPS)[number];

/** What a charge on a host with no order-family policy is decided on. */
export type HostChargeFacts<Status extends string> = {
  hostStatus: Status;
  /** The charge's own Bills and its split children's Bills. */
  billStatuses: readonly BillStatus[];
  /** The charge itself has a bill line, in any bill status (`orderChargeDeliveredPriceBillLock`). */
  chargeHasBillLine: boolean;
};

/**
 * CHARGES ON A HOST OUTSIDE THE ORDER FAMILY — buy-sell order, production run, lot, inventory transfer.
 * Field-blind, like the charges grid's default lock they replace:
 *
 *   view · repair incomplete allocation ─────────────────────────▶ allow
 *   edit(includedInDlvPrice) on a charge with a bill line ───────▶ ORDER_CHARGE_DELIVERED_PRICE_BILL_OPEN
 *   host status ∉ window (the host page's own predicate) ────────▶ `statusLock`
 *   add ─────────────────────────────────────────────────────────▶ allow
 *   edit(includedInDlvPrice) ────────────────────────────────────▶ allow (the api gates it on the bill LINE)
 *   anything else, own + child bills ∉ DRAFT/CANCELLED ──────────▶ ORDER_EXPENSE_BILL_LOCKED
 */
export const decideHostCharge = <Status extends string>(
  { editableStatuses, statusLock }: { editableStatuses: readonly Status[]; statusLock: Decision },
  facts: HostChargeFacts<Status>,
  action: Action<HostChargeField, HostChargeOp>
): Decision => {
  if (action.type === 'view') return allow();
  if (action.type === 'perform' && action.op === 'repair incomplete allocation') return allow();

  const changesDeliveredPrice = action.type === 'edit' && action.field === 'includedInDlvPrice';
  if (changesDeliveredPrice) {
    const billLine = orderChargeDeliveredPriceBillLock(facts.chargeHasBillLine);
    if (!billLine.allowed) return billLine;
  }

  if (!editableStatuses.includes(facts.hostStatus)) return statusLock;
  if (action.type === 'add' || changesDeliveredPrice) return allow();

  return orderChargeBillLock(facts.billStatuses, ORDER_EXPENSE_BILL_LOCKED, ORDER_CHARGE_BILL_LOCKED_MESSAGE);
};
